"use client";

import React, { Suspense, useMemo } from "react";
import { cn } from "@/lib/utils";
import FormattedDate from "@/components/global/countdown/FormattedDate";
import { useCountdown } from "@/lib/utilHooks";
import { useEffect } from "react";
import barText from "@/public/countdown/barText.svg";
import Image from "@/helpers/Image";
import { PortableTextBlock } from "sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PortableText } from "@portabletext/react";

export default function Countdown({
  dateTimeString,
  className,
  eventDateText,
  placesLeftText,
  placesLeftTextNumber,
  onlyCountdown,
}: {
  dateTimeString: string;
  className?: string;
  eventDateText?: string;
  placesLeftText?: PortableTextBlock[];
  placesLeftTextNumber?: PortableTextBlock[];
  onlyCountdown?: boolean;
}) {
  const { days, hours, minutes, seconds } = useCountdown(dateTimeString);

  useEffect(() => {
    console.log("dateTimeString", dateTimeString);
  }, [dateTimeString]);

  const progress = useMemo(() => {
    const targetDate = new Date(dateTimeString);
    const now = new Date();
    const msPerDay = 24 * 60 * 60 * 1000;
    const daysLeft = Math.ceil(
      (targetDate.getTime() - now.getTime()) / msPerDay,
    );
    const maxDays = 30;
    const progress = Math.max(
      0,
      Math.min(100, ((maxDays - daysLeft) / maxDays) * 100),
    );
    return progress;
  }, [dateTimeString]);

  return (
    <div
      className={cn(
        "flex flex-col justify-center items-center gap-[1.25rem] lg:gap-[2rem]",
        className,
      )}
    >
      <div className="w-full flex items-center">
        <Suspense
          fallback={
            <p className="text-white/90 text-[1.75rem] lg:text-[2.25rem] leading-none mb-0">
              Loading.
            </p>
          }
        >
          {!onlyCountdown && (
            <span
              className="countdownEnd text-white/80 text-[1.75rem] lg:text-[2rem] leading-none mb-0
            bg-white/[0.22] border border-white/30 rounded-full px-[0.6rem] pt-[0.6rem] pb-[0.3rem] lg:px-[1rem] lg:pt-[1rem] lg:pb-[0.5rem]"
            >
              {eventDateText ? eventDateText : "Event Datum:"}{" "}
              <FormattedDate
                dateTimeString={dateTimeString}
                format="DD.MM.YYYY"
              />
            </span>
          )}
        </Suspense>
      </div>

      <div className="countdownContainer flex items-center gap-[1rem] justify-between w-full">
        <div className="flex flex-col items-center justify-center gap-[.5rem] lg:gap-[1rem] min-w-[9rem] md:min-w-[10rem]">
          <p className="countdownText mb-0 text-white text-[7rem] sm:text-[7rem] lg:text-[11rem] font-normal leading-none tabular-nums">
            {days}
          </p>
          <p className="mb-0 text-white/60 text-[1.25rem] lg:text-[1.75rem] leading-none uppercase">
            Tage
          </p>
        </div>

        <div className="flex flex-col">
          <p className="mb-0 text-white/40 font-normal text-[4rem] lg:text-[7rem] leading-none font-helveticaNeue">
            :
          </p>
        </div>

        <div className="flex flex-col items-center justify-center gap-[.5rem] lg:gap-[1rem] min-w-[9rem] md:min-w-[10rem]">
          <p className="countdownText mb-0 text-white text-[7rem] sm:text-[7rem] lg:text-[11rem] font-normal leading-none tabular-nums">
            {hours}
          </p>
          <p className="mb-0 text-white/60 text-[1.25rem] lg:text-[1.75rem] leading-none uppercase">
            Stunden
          </p>
        </div>

        <div className="flex flex-col">
          <p className="mb-0 text-white/40 font-normal text-[4rem] lg:text-[7rem] leading-none font-helveticaNeue">
            :
          </p>
        </div>

        <div className="flex flex-col items-center justify-center gap-[.5rem] lg:gap-[1rem] min-w-[9rem] md:min-w-[10rem]">
          <p className="countdownText mb-0 text-white text-[7rem] sm:text-[7rem] lg:text-[11rem] font-normal leading-none tabular-nums">
            {minutes}
          </p>
          <p className="mb-0 text-white/60 text-[1.25rem] lg:text-[1.75rem] leading-none uppercase">
            Minuten
          </p>
        </div>

        <div className="flex flex-col">
          <p className="mb-0 text-white/40 font-normal text-[4rem] lg:text-[7rem] leading-none font-helveticaNeue">
            :
          </p>
        </div>

        <div className="flex flex-col items-center justify-center gap-[.5rem] lg:gap-[1rem] min-w-[9rem] md:min-w-[10rem]">
          <p className="countdownText mb-0 text-white text-[7rem] sm:text-[7rem] lg:text-[11rem] font-normal leading-none tabular-nums">
            {seconds}
          </p>
          <p className="mb-0 text-white/60 text-[1.25rem] lg:text-[1.75rem] leading-none uppercase">
            Sekunden
          </p>
        </div>
      </div>

      {!onlyCountdown && (
        <div className="countdownProgressbar flex flex-col w-full">
          <div className="">
            <div className="w-full">
              <div className="overflow-hidden w-full h-[1rem] bg-white/10">
                <div
                  className="h-full transition-all duration-300 bg-[#64E63F52] relative"
                  style={{
                    width: `${progress}%`,
                    backgroundImage:
                      "linear-gradient(135deg, rgb(255 255 255 / 0%) 25%, #00000000 25%, #00000000 50%, rgb(255 255 255 / 25%) 50%, rgb(255 255 255 / 0%) 55%, #00000000 75%, transparent)",
                    backgroundSize: "3.5rem 1rem",
                  }}
                >
                  <div className="absolute top-0 right-0 h-full w-[0.5rem] bg-[#A8E596]"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-[0.25rem] mb-[0.5rem]">
            <div className="w-full">
              <div className="overflow-hidden w-full h-[0.125rem]">
                <div
                  className="h-full rounded-full transition-all duration-300 bg-[#A8E596]"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          </div>
          <Image src={barText} alt="" className="!w-full" />
        </div>
      )}

      {placesLeftText && placesLeftTextNumber && (
        <div className="flex items-center justify-between w-full">
          <div
            className="richtext lg:richtext-start
            prose-p:text-[1rem] lg:prose-p:text-[1.75rem] prose-p:text-white"
          >
            <PortableText
              value={placesLeftText}
              components={richTextComponent}
            />
          </div>
          <div
            className="richtext lg:richtext-start rounded-full border border-white/[0.15] px-[0.85rem] lg:px-[1.5rem] pt-[0.6rem] pb-[0.3rem] lg:pt-[1rem] lg:pb-[0.5rem]
            prose-p:text-[1.25rem] lg:prose-p:text-[2rem] prose-p:text-white/80"
          >
            <PortableText
              value={placesLeftTextNumber}
              components={richTextComponent}
            />
          </div>
        </div>
      )}
    </div>
  );
}
